/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2020, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { normalize } from 'normalizr';

import { selectIsAccessUpdatesEnabled } from '@ibm-watson/shared/modules/ia/store/features/selectors';
import { isCacheExpired } from '@ibm-watson/shared/modules/ia/store/helper';
import * as messagesActions from '@ibm-watson/shared/modules/ia/store/messages/actions';
import * as operations from '@ibm-watson/shared/modules/ia/store/operations';
import { isCpd } from '@ibm-watson/shared/modules/ia/util/config';
import {
  createdObjectEvents,
  deletedObjectEvents
} from '@ibm-watson/shared/modules/ia/util/trackers/segment';
import { Language } from '../../../../ts/skill';
import { selectIsNewIa } from '../../../ia/store/instance/selectors';
import { getAssistantId } from '../../../ia/store/session/selectors';
import * as variablesAPI from '../../api/variables';
import * as actionTypes from './actionTypes';
import {
  systemVariablesSchema,
  variableSchema,
  variablesSchema
} from './schema';
import { getVariable as getVariableSelector } from './selectors';

// --- listSystemVariables ---
export const listSystemVariablesStart = () => ({
  type: actionTypes.LIST_SYSTEM_VARIABLES_START
});

export const listSystemVariablesSuccess = response => ({
  type: actionTypes.LIST_SYSTEM_VARIABLES_SUCCESS,
  response,
  payload: {
    operation: operations.LIST
  }
});

export const listSystemVariablesError = () => ({
  type: actionTypes.LIST_SYSTEM_VARIABLES_ERROR,
  entities: {
    variables_system: {}
  }
});

export const listSystemVariables =
  (skillLanguage: Language) => async dispatch => {
    dispatch(listSystemVariablesStart());
    try {
      const params = {
        language: skillLanguage
      };
      let response = await variablesAPI.listSystemVariables(params);
      // channel_name variable should not be shown in cp4d
      if (isCpd) {
        response = response.filter(v => v.system_variable !== 'channel_name');
      }
      const normalizedResponse = normalize(response, systemVariablesSchema);
      dispatch(listSystemVariablesSuccess(normalizedResponse));
      return response;
    } catch (error) {
      dispatch(listSystemVariablesError());
      dispatch(messagesActions.addErrorMessage(error));
      throw error;
    }
  };

// --- listVariables ---

export const listVariablesStart = (skillId: string) => ({
  type: actionTypes.LIST_VARIABLES_START,
  skillId
});

export const listVariablesSuccess = (skillId: string, response) => ({
  type: actionTypes.LIST_VARIABLES_SUCCESS,
  skillId,
  response,
  payload: {
    operation: operations.LIST
  }
});

export const listVariablesError = () => ({
  type: actionTypes.LIST_VARIABLES_ERROR,
  entities: { variables: {} }
});

export const listVariables =
  (skillId: string, reactivateSession?: boolean) =>
  async (dispatch, getState) => {
    dispatch(listVariablesStart(skillId));
    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to listVariable
    const assistantId = getAssistantId(getState());
    const isNewIA = selectIsNewIa(getState());
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(getState());
    try {
      const response = await variablesAPI.listVariables(
        skillId,
        useNewIAUpdatedAccess,
        assistantId,
        reactivateSession
      );
      const normalizedResponse = normalize(response.variables, variablesSchema);
      dispatch(listVariablesSuccess(skillId, normalizedResponse));
      return response;
    } catch (err) {
      dispatch(listVariablesError());
      dispatch(messagesActions.addErrorMessage(err));
      throw err;
    }
  };

export const listVariablesIfRequired =
  (skillId, reactivateSession) => async (dispatch, getState) => {
    const { skillId: cachedSkillId, listFetched } = getState()?.variables;

    if (skillId === cachedSkillId && !isCacheExpired(listFetched)) {
      return Promise.resolve({});
    }

    return dispatch(listVariables(skillId, reactivateSession));
  };

// --- createVariable ---

export const createVariableStart = (skillId, variable) => ({
  type: actionTypes.CREATE_VARIABLE_START,
  variableId: variable.variable,
  variable,
  skillId
});

export const createVariableSuccess = response => ({
  type: actionTypes.CREATE_VARIABLE_SUCCESS,
  response,
  payload: {
    operation: operations.CREATE
  }
});

export const createVariableError = () => ({
  type: actionTypes.CREATE_VARIABLE_ERROR
});

export const createVariable =
  (skillId: string, variable: any) => async (dispatch, getState) => {
    dispatch(createVariableStart(skillId, variable));

    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to createVariable
    const assistantId = getAssistantId(getState());
    const isNewIA = selectIsNewIa(getState());
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(getState());
    try {
      const newVariable = await variablesAPI.createVariable(
        skillId,
        variable,
        useNewIAUpdatedAccess,
        assistantId
      );
      dispatch(
        createVariableSuccess(normalize({ ...newVariable }, variableSchema))
      );

      createdObjectEvents.globalVariable({
        object: skillId
          ? `${skillId}--${newVariable.variable}`
          : newVariable.variable,
        resultValue: 'success',
        successFlag: true
      });
    } catch (error) {
      dispatch(messagesActions.addErrorMessage(error));
      dispatch(createVariableError());
      throw error;
    }
  };

// --- deleteVariable ---

export const deleteVariableStart = (skillId, variableId) => ({
  type: actionTypes.DELETE_VARIABLE_START,
  variableId,
  skillId
});

export const deleteVariableSuccess = response => ({
  type: actionTypes.DELETE_VARIABLE_SUCCESS,
  response,
  payload: {
    operation: operations.DELETE
  }
});

export const deleteVariableError = () => ({
  type: actionTypes.DELETE_VARIABLE_ERROR
});

export const deleteVariable =
  (skillId: string, variableId: string) => async (dispatch, getState) => {
    dispatch(deleteVariableStart(skillId, variableId));
    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to deleteVariable
    const assistantId = getAssistantId(getState());
    const isNewIA = selectIsNewIa(getState());
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(getState());
    try {
      await variablesAPI.deleteVariable(
        skillId,
        variableId,
        useNewIAUpdatedAccess,
        assistantId
      );
      dispatch(
        deleteVariableSuccess(
          normalize({ variable: variableId }, variableSchema)
        )
      );

      // track successful global variable deletion
      deletedObjectEvents.globalVariable({
        object: skillId ? `${skillId}--${variableId}` : variableId,
        resultValue: 'success',
        successFlag: true
      });
      return variableId;
    } catch (error) {
      dispatch(messagesActions.addErrorMessage(error));
      dispatch(deleteVariableError());
      throw error;
    }
  };

// --- updateVariable ---

export const updateVariableStart = () => ({
  type: actionTypes.UPDATE_VARIABLE_START
});

export const updateVariableSuccess = response => ({
  type: actionTypes.UPDATE_VARIABLE_SUCCESS,
  response,
  payload: {
    operation: operations.UPDATE
  }
});

export const updateVariableError = () => ({
  type: actionTypes.UPDATE_VARIABLE_ERROR
});

export const updateVariable =
  (skillId, existingVariableId, updatedVariable) =>
  async (dispatch, getState) => {
    dispatch(updateVariableStart());
    try {
      // Use revised API for Access Control improvements if enabled & isNewIA
      // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to updateVariable
      const assistantId = getAssistantId(getState());
      const isNewIA = selectIsNewIa(getState());
      const useNewIAUpdatedAccess =
        isNewIA && selectIsAccessUpdatesEnabled(getState());
      const existingVariable = getVariableSelector(
        getState(),
        existingVariableId
      );
      const newlyUpdated = await variablesAPI.updateVariable(
        skillId,
        existingVariableId,
        updatedVariable,
        useNewIAUpdatedAccess,
        assistantId
      );
      updatedVariable = { ...existingVariable, ...newlyUpdated };
      if (!newlyUpdated.initial_value) {
        delete updatedVariable.initial_value; // prevent old initial_value from being copied over if we delete an initial_value
      }
      if (!newlyUpdated.default_value) {
        delete updatedVariable.default_value; // prevent old default_value from being copied over if we delete an initial_value
      }
      dispatch(
        updateVariableSuccess(normalize(updatedVariable, variableSchema))
      );
    } catch (error) {
      dispatch(messagesActions.addErrorMessage(error));
      dispatch(updateVariableError());
      throw error;
    }
  };
